/* global _, $, Rx, Redux, ReduxObservable, virtualDom, Promise, ProgressBar, MobileDetect, console */

import { ACTIONS } from "./_actions";
import {
	convertName,
	getEnvironment,
	getOptionsMatrixRegionId,
	getCulture,
	isMobileDevice,
	getNumFrames,
	imgixRequest,
	getDimensions,
	conditionalValue
} from "./_utilities";
import intents from "./_exterior-builder-intents";
import { rotateTruck } from "./_exterior-methods";
import { setupSocialShareHandler } from "./_social-share";
import { store, store$ } from "./_config-store";

// import this way so that it can be removed from production
// since webpack doesn't tree shake this out otherwise
const createLogger = (process.env.NODE_ENV !== 'production')
	? require("redux-logger").createLogger
	: store => next => action => next(action);
const { createStore, applyMiddleware, compose } = Redux;
const { createEpicMiddleware, combineEpics } = ReduxObservable;
const h = virtualDom.h;


/**
 * Build truck configurator
 * @param selector - dom selector for container
 * @param path - path to the images
 * @param layers - array of layerNames
 * @param useWorker - bool
 * @returns {}
 */

let exteriorBuilder = {};

export function getExteriorBuilder() {
	return exteriorBuilder;
}

export function setupExteriorBuilder(selector, uiState, l, useWorker, rules) {
	let isMobile = isMobileDevice();
	let $container = $(selector);
	let $progressBarMask = $('<div class="progress-bar-mask"><div class="progress-bar-line"></div></div>').appendTo($container);
	let $progressBarLine = new ProgressBar.Line($progressBarMask.find(".progress-bar-line")[0], {
		strokeWidth: 4,
		easing: 'linear',
		duration: 100,
		color: '#5482ab',
		trailColor: '#e5e5e5',
		trailWidth: 4,
		svgStyle: { width: '100%', height: '100%' },
		text: {
			style: {
				// Text color.
				// Default: same as stroke color (options.color)
				color: '#5482ab',
				position: 'absolute',
				left: '50%',
				top: '5px',
				padding: 0,
				margin: 0,
				transform: 'translateX(-50%)'
			},
			autoStyleContainer: false
		},
		step: function (state, bar) {
			if (!isMobile) {
				bar.setText(Math.round(bar.value() * 100) + ' %');
			}
		}
	});
	let imageMaxWidth = 1365;
	let imageMaxHeight = 910;
	let aspectRatio = imageMaxHeight / imageMaxWidth;
	let VTree, VNode;
	let {angleMap, exterior: layerMap, alwaysOn} = rules;
	let possibleFrames = 24;
	let numFrames = getNumFrames(isMobile);
	let maxFrames = numFrames;
	let exteriorBuilderState = {
		uiState,
		container: $container,
		hideIndex: numFrames - 1,
		showIndex: 0,
		width: 0,
		height: 0,
		layers: [],
		possibleFrames,
		numFrames,
		maxFrames,
		userInteracted: false,
		layersVDom: [],
	};


	exteriorBuilder.el = $('<div class="layers"></div>').appendTo($container);

	// cache the results of getting the filenames
	let requestFilenames = _.memoize(function requestFilenames(path, layerName) {
		let region = getOptionsMatrixRegionId(),
			culture = getCulture(),
			env = getEnvironment();
		const root = env === 'DEV' ? 'https://freightlinertrucks-dev.azurewebsites.net' : '/umbraco/Api';
		const environment = env === 'DEV' ? '&env=local' : '';
		const url = `${root}/ConfiguratorImages?dir=${path}&region=${region}&culture=${culture}&image=${layerName}${environment}`;
		return $.ajax({
			url: url,
			method: 'GET',
			dataType: "json"
		});
	}, (path, layerName) => {
		return path + "/" + layerName;
	});

	function getFileNames(path, layerName) {
		let adjustedPath = path;
		let adjustedLayerName = layerName;
		let pathRegex = /(.+\/)(.+)/g;
		// if the layer map has a path use it instead of truck model based one
		// allows reuse of images
		if (layerName.indexOf("/") !== -1) {
			let splitPath = pathRegex.exec(path);
			adjustedPath = `${splitPath[1]}${layerName.split("/")[0]}`;
			adjustedLayerName = layerName.split("/")[1];
		}
		var defer = new Promise(function (resolve, reject) {
			requestFilenames(adjustedPath, adjustedLayerName).then(function (filenames) {
				resolve(_.chain([filenames]).flatten().orderBy('path').value());
			}, function (error) {
				// handle the case where the ajax request errors - don't want to cache the error
				delete requestFilenames.cache.__data__.string[adjustedPath + adjustedLayerName];
				reject(error);
			});
		});
		return defer;
	}

	function getLayerImages(path, layerName) {
		let defer = new Promise(function (resolve, reject) {
			Rx.Observable.fromPromise(getFileNames(path, layerName))
				.subscribe(function handleNext(filenames) {
					resolve(_.map(filenames, function (filename) {
						
						var dims = getDimensions({ ...filename, imageMaxWidth, imageMaxHeight });
						
						return {
							"src": filename.path,
							"width": dims.width,
							"height": dims.height,
							"top": dims.y,
							"left": dims.x
						};
					}));
				}, function handleError(e) {
					reject(e);
				}, function handleComplete() {
					// console.log(`get layer images for ${layerName} and ${path} complete`);
				});
		});
		return defer;
	}

	/**
	 * load and return an image by url
	 * @param object with src property
	 * @returns {Promise}
	 */
	function loadImage(image, color) {
		return new Promise((resolve, reject) => {
			let img = new Image();
			img.setAttribute('crossOrigin', 'anonymous');
			img.src = buildImgixURL({src:image.src,color});
			img.onload = () => {
				// console.log("resolve load Image", image.src)
				resolve(image);
			};
			img.onerror = () => {
				// TODO this stops the load from breaking, but what do we do with the missing image?
				
				if (window.dataLayer) {
					window.dataLayer.push({
						event: "configurator_load_image_error",
						data: {
							image,
							color
						}
					});
				}
				resolve(image);
			};
		});
	}

	function sortImages(a, b) {
		var srcA = a.src.toUpperCase(); // ignore upper and lowercase
		var srcB = b.src.toUpperCase(); // ignore upper and lowercase
		if (srcA < srcB) {
			return -1;
		}
		if (srcA > srcB) {
			return 1;
		}
		// names must be equal
		return 0;
	}

	function getCanvasDimensions() {
		let configuratorViewers = document.querySelector("#configurator-viewers");
		let width = configuratorViewers.clientWidth;
		let height = configuratorViewers.clientHeight;
		if (width * aspectRatio < height) {
			return {
				width: width,
				height: width * aspectRatio,
			};
		} else {
			return {
				width: height / aspectRatio,
				height: height,
			};
		}
	}

	/**
	 * return vnode of image layer
	 * @param layer
	 * @param showIndex
	 */
	function getImageLayerVDom(layerData, showIndex, frames, color) {
		let images,
			{componentName} = layerData;
		if (layerData.images.length === 0) {
			images = _.range(0, frames).map(n => ({
				src: ""
			}));
		} else if (layerData.images.length === 1) {
			images = _.range(0, frames).map(n => ({
				src: ""
			}));
			images[showIndex] = layerData.images[0];
		} else {
			images = layerData.images.sort(sortImages);
		}
		
		return h(`div.${color ? "paint" : "image"}-layer`, {
			attributes: {
				"data-layer": componentName,

			}
		}, _.chain(images).map(img => {
			img.src = img.src || "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";
			return img;
		}).map((img, i) => {
			let dims = getDimensions({path:img.src,imageMaxWidth,imageMaxHeight});
			let dataUrl = img.src.indexOf("data:image") > -1;
			let src = dataUrl
				? img.src
				: buildImgixURL({src:img.src,color,componentName});
			

			return h("img.layer-angle", {
				attributes: {
					"src": src,
					"crossOrigin": "anonymous",
					"draggable": "false",
					//"metadata": JSON.stringify({x:dims.x,y:dims.y,w:dims.width,h:dims.height}),
					"style": `width: ${dims.width}px; height: ${dims.height}px; top: ${dims.y}px; left: ${dims.x}px; opacity: ${showIndex === i ? 1 : 0}; z-index: ${getLayerIndex({componentName, src, layerMap})};`
				}
			});
		}).value());
	}

	function buildImgixURL({src,color,componentName}){
		let widthMobile = getDimensions({path:src,imageMaxWidth,imageMaxHeight}).width,
			base = /.+cab-[0-9].*\.png/.test(src),
			format = base ? 'jpg' : src.split('.').pop().toLowerCase();

		return imgixRequest({
			path: src,
			options: {
				auto: ['format','compress'],
				fm: ['jp2',format === 'jpg' ? 'jpg' : 'png8'],
				bg: base ? 'e9e9e9' : undefined,
				blend: color ? color.replace("#", "") : undefined,
				bm: color ? 'overlay' : undefined,
				w: isMobile ? widthMobile : undefined,
				mask: color ? {
					path: src,
					options: {
						gam: -100,
					}
				} : undefined
			}
		});
	}

	/**
	 * render the vdom
	 * @param state
	 */
	function render(state) {
		// create/update image and paint layers and hotspots
		// console.log("update exterior dom", state);
		let canvasDims = getCanvasDimensions();
		// console.log("render", state.layersVDom);
		return h("div.vdom" + (!state.layersVDom ? ".hidden" : ""), {
			attributes: {
				"style": `width: ${canvasDims.width}px; height: ${canvasDims.height}px;`
			}
		}, _.map(state.layersVDom, layer => {
			if (_.isArray(layer.layerData)) {
				return [
					getImageLayerVDom(layer.layerData[0], state.showIndex, state.maxFrames),
					getImageLayerVDom(layer.layerData[1], state.showIndex, state.maxFrames, state.color)
				];
			}
			return getImageLayerVDom(layer.layerData, state.showIndex, state.maxFrames);
		}));
	}

	/**
	 *
	 * @param state
	 * @param $container
	 * @param path
	 * @param componentName
	 * @param layerName
	 * @param color
	 * @param layerIndices
	 * @returns {Promise<any>}
	 */
	function createImageLayer(state, $container, path, componentName, layerName, color, layerIndices) {
		let layerContainer = {
			images: [],
			componentName
		};
		
		return new Promise((resolve, reject) => {
			if (!layerName) {
				resolve(layerContainer);
				return;
			}
			Rx.Observable.fromPromise(getLayerImages(path, layerName))
				.subscribe(images => {
					
					if (images.length === 0) {
						resolve(layerContainer);
						return;
					}
					Rx.Observable.from(images
						.filter((img, i) => {
							return _.includes(layerIndices, i);
						})
					).mergeMap(image => {
						return Rx.Observable.fromPromise(loadImage(image, color))
							.retry(3).catch(e => {
								return Rx.Observable.of(image);
							});
					}).bufferCount(layerIndices.length)
						.subscribe(images => {
							resolve({
								images,
								componentName
							});
						});
				}, function handleError(e) {
					console.error("error", e);
					reject(e);
				}, function handleComplete() {
					// console.log("creating image layer " + componentName + " complete");
				});
		});
	}

	function createLayerGenerator(state, layers, layerIndices) {

		let color = state.color;
		let path = state.path;

		let container = state.container;
		
		return Rx.Observable.from(layers)
			.concatMap(function (layer) {
				if (layer[2]) {
					return Rx.Observable.fromPromise(
						Promise.all([
							createImageLayer(state, container, path, layer[0], layer[1], null, layerIndices),
							createImageLayer(state, container, path, layer[0], layer[2], color, layerIndices)
						])
					);
				} else {
					return Rx.Observable.fromPromise(createImageLayer(state, container, path, layer[0], layer[1], null, layerIndices));
				}
			})
			.scan(function (acc, x, i) {
				let min = layerIndices.length > 1 ? 0.2 : 0;
				let max = layerIndices.length > 1 ? 1 : 0.2;
				let layerData = updateProgress(i, layers.length - 1, x, min, max);
				layerData.path = path;
				layerData.componentName = layerData.layerData.componentName || layerData.layerData[0].componentName;
				return layerData;
			}, { layerData: 0, progress: 0, r: false })
			.bufferCount(layers.length);
	}

	function getLayerInfo(layer, layerKey, state, optionKey, force) {
		
		let truckModelCab = convertName(state.truckModel + "-" + state.truckCab),
			{conditions} = layerMap[layerKey],
			settingValue = state.exteriorBuildSettings[layerKey],
			renderedValue = conditionalValue({state,standard:optionKey || settingValue,conditions});


		let imageName = 
			settingValue && layer.name === "cab" && !conditions ?
				`${truckModelCab}-cab` :
			settingValue || (optionKey && force) ?
				`${truckModelCab}-${layer.name}-${renderedValue}` : 
			false;		

		let imageUrl = imageName ? imageName + ".png" : false;
		
		let paintImageUrl = imageUrl && layer.paint ? 
			typeof layer.paint === 'boolean' ?
				imageName + "-paint-gray.png" : 
				`${truckModelCab}-${conditionalValue({state,standard:optionKey || settingValue,conditions:{'*':['temp',layer.paint]}})}-paint-gray.png` :
			false;

		return [layer.name, imageUrl, paintImageUrl, layer.index];
	}

	function getLayerIndex({componentName, src,layerMap}){
		let {indexOver = [], indexUnder = [], index} = (Object.keys(layerMap).map( key => layerMap[key] ).find(({name}) => name === componentName) || {});
		return indexOver.find( str => src.indexOf(`-${str}.png`) !== -1) ? 
			100 :
			indexUnder.find( str => src.indexOf(`-${str}.png`) !== -1) ?
			0 :
			index;
	}

	function mapLayerInfoWithReuseMap(layer, state) {
		// map the layers to existing (reused) image in the map file
		return _.map(layer, (layerPart, i) => {
			if (layerPart === false || _.isNumber(layerPart) || i === 0) {
				return layerPart;
			}
			if (state.exteriorMap[layerPart]) {
				return state.exteriorMap[layerPart].replace(".png", "");
			}
			return layerPart.replace(".png", "");
		});
	}	

	function getLayers(state) {
		// create the layers, order and layer types are hard-coded
		return _.chain(layerMap)
			.omit(({name}) => !name)
			.map((layer, layerKey) => {
				return !!alwaysOn[layerKey] && state.truckCab ? getLayerInfo(layer, layerKey, state, alwaysOn[layerKey], true) : getLayerInfo(layer, layerKey, state);
			})
			.map(layer => {
				//console.log('GET LAYERS 2',layer);
				return mapLayerInfoWithReuseMap(layer, state);
			})
			.value();
	}

	function hideProgressBar() {
		$progressBarMask.hide();
		$progressBarMask.addClass("remainder");
		$progressBarLine.set(0);
	}

	/**
	 *
	 * @param i - index
	 * @param l - length
	 * @param x - current
	 * @param c - complete
	 * @param r - resize
	 * @param min
	 * @param max
	 * @returns {{current: *, progress: number, r: *}}
	 */
	function updateProgress(i, l, x, min = 0, max = 1) {
		var target = l > 0 ? l : 1,
			progress = i === 0 && l === 0 ? 1 : min + ((i / target) * (max - min)),
			acc = {
				layerData: x,
				progress: progress
			};

		if (progress >= 0) {
			$progressBarMask.show();
		}

		if (progress === 0) {
			$progressBarLine.set(0);
		} else {
			$progressBarLine.set(min);
			$progressBarLine.animate(progress, () => {
				if (progress === 1) {
					hideProgressBar();
				}
			});
		}
		return acc;
	}

	/**
	 * get layer data used to build vdom
	 * @param currState
	 * @param numRuns
	 * @returns {*}
	 */
	function getLayersVDom$(prevState, currState) {
		// console.log("getting the vdom layers", prevState.layers, currState.layers);
		// console.log("layergen", currState, _.range(0, currState.possibleFrames, stepSize),  currState.possibleFrames, currState.numFrames, currState.showIndex)
		let stepSize = currState.possibleFrames / currState.numFrames,
			observables;

		// get the layers
		let layers = getLayers(currState.uiState)
		// remove layers that have no images set
			.filter(layer => {
				return layer[1] || layer[2];
			});

		observables = [
			createLayerGenerator(currState, layers, [_.range(0, currState.possibleFrames, stepSize)[currState.showIndex]]),
			createLayerGenerator(currState, layers, _.range(0, currState.possibleFrames, stepSize))
		];

		// console.log("layers to generate on run:", numRuns, currState.layers, stepSize, numFrames, observables);
		return Rx.Observable.concat.apply(null, observables);
	}

	function generatePath(uiState) {
		return 'exterior/'
			+ convertName(uiState.truckModel + "-" + uiState.truckCab);
	}

	function exteriorBuilderReducers(state = exteriorBuilderState, action) {
		switch (action.type) {
			case ACTIONS.EXTERIOR_VIEW_STATE_UPDATE: {
				return Object.assign({}, state, {
					path: generatePath(action.data.state, isMobile),
					uiState: action.data.state
				});
			}
			case ACTIONS.UPDATE_EXTERIOR_ACTIVE_FRAME: {
				return Object.assign({}, state, {
					showIndex: action.data.showIndex,
					hideIndex: action.data.hideIndex,
					userInteracted: true,
				});
			}
			case ACTIONS.UPDATE_EXTERIOR_VIEW_HOTSPOTS: {
				return Object.assign({}, state, {
					uiState: action.data.state,
				});
			}
			case ACTIONS.UPDATE_EXTERIOR_VIEW_LAYERS: {
				return Object.assign({}, state, {
					path: action.data.path,
					layers: action.data.layers,
					color: action.data.color,
					layersVDom: action.data.layersVDom,
					reset: false
				});
			}
			case ACTIONS.PRECACHE_ACTIVE_OPTION: {
				return Object.assign({}, state, {
					activeOption: state.data,
				});
			}
			case ACTIONS.RESIZE_EXTERIOR: {
				return Object.assign({}, state, {
					width: action.data.width,
					height: action.data.height,
				});
			}
			case ACTIONS.RESET_EXTERIOR_VIEW: {
				return Object.assign({}, state, {
					// reset: true,
					// layers: [],
					// layersVDom: [],
					// showIndex: 0,
					// hideIndex: 1,
					// path: '',
					uiState: action.data.state,
					userInteracted: false,
				});
			}
			case ACTIONS.CAB_CHANGE: {
				return Object.assign({}, state, {
					layers: [],
					layersVDom: [],
					showIndex: 0,
					hideIndex: 1,
					path: generatePath(action.data.uiState, isMobile),
					uiState: action.data.uiState,
					userInteracted: false,
				});
			}
			default: {
				return state;
			}
		}
	}

	const preCacheActiveOption$ = (action$, store) => action$.ofType(ACTIONS.PRECACHE_ACTIVE_OPTION)
		.do(action => console.log("active option", action, layerMap))
		.switchMap(action => {
			let state = exteriorBuilder.store.getState();
			let layerOption = action.data[0] && layerMap[action.data[0].attributes.name] && layerMap[action.data[0].attributes.name].name;
			if (!layerOption) {
				return Rx.Observable.empty();
			}
			let layerName = layerOption.name;
			let layerNames = _.chain(action.data)
				.map(option => {
					let layerKey = option.attributes.name;
					let layer = layerMap[layerKey];
					let optionKey = option.attributes["data-key"];
					return getLayerInfo(layer, layerKey, state.uiState, optionKey);
				})
				.map(layer => mapLayerInfoWithReuseMap(layer, state.uiState))
				.flatten()
				.compact()
				.filter(image => image !== layerName)
				.filter(image => !_.isNumber(image))
				.value();
			
			return Rx.Observable.from(layerNames);
		})
		.mergeMap(layerImage => {
			let state = exteriorBuilder.store.getState();
			return Rx.Observable.fromPromise(getLayerImages(state.path, layerImage));
		})
		.filter(filenames => filenames.length > 0)
		.map(filenames => filenames[exteriorBuilder.store.getState().showIndex])
		.mergeMap(src => loadImage(src))
		.ignoreElements();

	const rotateToShowOption$ = (action$, store) => action$.ofType(ACTIONS.PRECACHE_ACTIVE_OPTION)
		.switchMap(() => {
			let state = exteriorBuilder.store.getState();
			let activeOptionAngle = angleMap[state.uiState.activeOption];
			return activeOptionAngle
				? rotateTruck(-1, state, activeOptionAngle)
				: Rx.Observable.empty();
		})
		.delay(350)
		// rotate the truck to show the option better
		.map(({ showIndex, hideIndex }) => ({
			type: ACTIONS.UPDATE_EXTERIOR_ACTIVE_FRAME,
			data: {
				showIndex,
				hideIndex
			}
		}));

	/**
	 * re-render exterior view based on configuration changes
	 * @param action$
	 * @param store
	 */
	const updateViewerEpic$ = (action$, s) => action$.ofType(ACTIONS.EXTERIOR_VIEW_STATE_UPDATE)
		.map(() => Object.assign({}, exteriorBuilder.store.getState()))
		.filter(state => state.uiState.actionType !== ACTIONS.RESIZE_EXTERIOR)
		.filter(state => {
			//console.log('UPDATE VIEWER FILTER',state.uiState.actionType,state.uiState.view);
			return (state.uiState.actionType === ACTIONS.EXTERIOR_BUILD_CHANGE
				|| state.uiState.actionType === ACTIONS.MISC_BUILD_CHANGE
				|| state.uiState.actionType === ACTIONS.VIEW_OPTION_CHANGE
				|| state.uiState.actionType === ACTIONS.BUILD_SETTINGS_RESTORE
				|| state.uiState.actionType === ACTIONS.CAB_CHANGE)
				&& state.uiState.view !== "interior";
		})
		.startWith(exteriorBuilderState)
		.map(state => {
			
			return Object.assign({}, state, {
				path: generatePath(state.uiState, isMobile),
				layers: getLayers(state.uiState),
				color: state.uiState.optionsMatrix
					? _.find(
						state.uiState.optionsMatrix.Exterior.Color,
						{ "Key": state.uiState.exteriorBuildSettings.Color }).Data
					: "",
			});
		})
		.distinctUntilChanged((prev, curr) => {
			
			return _.isEqual(prev.layers, curr.layers)
				&& prev.color === curr.color
				&& prev.path === curr.path;
		})
		// .do(state => console.log("layers here!!", state))
		.pairwise()
		.switchMap(([prevState, currState], i) => {
			// console.log("update layers vdom", prevState.layers, currState.layers);
			return getLayersVDom$(prevState, currState);
		})
		// .do(layersVDom => console.log("layers here!!2", layersVDom))
		.filter(layersVDom => layersVDom.length > 0)
		.map(layersVDom => {
			// console.log("process layers vdom", layersVDom);
			let uiState = exteriorBuilder.store.getState().uiState;
			return {
				type: ACTIONS.UPDATE_EXTERIOR_VIEW_LAYERS,
				data: {
					path: generatePath(uiState, isMobile),
					layers: getLayers(uiState),
					color: uiState.optionsMatrix
						? _.find(uiState.optionsMatrix.Exterior.Color, { "Key": uiState.exteriorBuildSettings.Color }).Data
						: "",
					layersVDom
				}
			};
		});

	const teaserTruckRotationEpic$ = (action$, s) => action$.ofType(ACTIONS.UPDATE_EXTERIOR_VIEW_LAYERS)
		.map(() => Object.assign({}, exteriorBuilder.store.getState()))
		.startWith(exteriorBuilderState)
		.pairwise()
		.scan((acc, [p, c]) => {
			if (p.path !== c.path) {
				acc = [];
			} else {
				acc.push(c);
			}
			return acc;
		}, [])
		.filter(a => a.length === 1)
		.switchMap(() => {
			return rotateTruck(-1, exteriorBuilder.store.getState(), 315);
		})
		.map(({ showIndex, hideIndex }) => ({
			type: ACTIONS.UPDATE_EXTERIOR_ACTIVE_FRAME,
			data: {
				showIndex,
				hideIndex
			}
		}));

	const exteriorBuilderEpics = combineEpics(
		preCacheActiveOption$,
		rotateToShowOption$,
		updateViewerEpic$,
		teaserTruckRotationEpic$
	);
	const exteriorBuilderEpicMiddleware = createEpicMiddleware(exteriorBuilderEpics);

	const middleware = [exteriorBuilderEpicMiddleware];
	if (process.env.NODE_ENV !== `production`) {
		middleware.push(createLogger({
			collapsed: true
		}));
	}

	exteriorBuilder.store = createStore(exteriorBuilderReducers,
		compose(
			applyMiddleware(
				...middleware
			),
			window.devToolsExtension && process.env.NODE_ENV === `development` ? window.devToolsExtension() : (f => f)
		));
	exteriorBuilder.store$ = Rx.Observable.from(exteriorBuilder.store);

	intents($container).subscribe(action => {
		exteriorBuilder.store.dispatch(action);
	});


// init the render process
	VTree = render(exteriorBuilderState);
	VNode = virtualDom.create(VTree);
	exteriorBuilder.el.html(VNode);

	exteriorBuilder.store$
		.filter(state => state.uiState.dataReady)
		.filter(state => state.actionType !== ACTIONS.CAB_RESET)
		.map(render)
		.startWith(VTree)
		.pairwise()
		.do(([a, b]) => {
			let patches = virtualDom.diff(a, b);
			VNode = virtualDom.patch(VNode, patches);
		})
		// .map(() => {
		// 	return exteriorBuilder.store.getState();
		// })
		// // rotate the truck as a teaser for the rotation functionality
		// .switchMap(() => {
		// 	return rotateTruck(-1, exteriorBuilder.store.getState(), 315);
		// })
		.subscribe(({ showIndex, hideIndex }) => {
			// exteriorBuilder.store.dispatch({
			// 	type: ACTIONS.UPDATE_EXTERIOR_ACTIVE_FRAME,
			// 	data: {
			// 		showIndex,
			// 		hideIndex
			// 	}
			// });
		});

	setupSocialShareHandler();

	return exteriorBuilder;
}